import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { first } from 'rxjs/operators';
import { AuthService } from 'src/app/core/http-services/auth.service';
import { AzureNamespacesService } from 'src/app/core/http-services/azure-namespaces.service';
import { AzureOperationsService } from 'src/app/core/services/azure-operations.service';
import { AzureTokenService } from 'src/app/core/services/azure-token.service';
import { EditNamespaceComponent } from 'src/app/modules/edit-namespace/edit-namespace.component';
import { ConfirmationDialogService } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.service';
import { PAGE_RESULT_SIZE } from 'src/app/shared/constants';
import { AzureNamespaceDisplay, KeysForANamespace, UpdateKeysRequest } from 'src/app/shared/models/azure-namespaces.model';
import { AzureErrorResponse } from 'src/app/shared/models/azure-token.model';
import { Pagination } from 'src/app/shared/models/pagination.model';
import { SpecialRoles } from 'src/app/shared/models/signin.model';
import { GeneralFunctions } from '../../shared/general-functions';

@Component({
  selector: 'app-manage-namespaces',
  templateUrl: './manage-namespaces.component.html',
  styleUrls: ['./manage-namespaces.component.css']
})
export class ManageNamespacesComponent implements OnInit {
  pageNo: number = 0;
  pager: Pagination = new Pagination();
  namespaces: AzureNamespaceDisplay[] = new Array();
  totalRecords: number = 0;
  successMessage: string = '';
  errorMessage: string = '';
  isShowSuccess: boolean = false;
  isShowError: boolean = false;
  roles: SpecialRoles;
  filterId: number = 0;

  constructor(private azureNamespaceService: AzureNamespacesService,
    private modalService: NgbModal,
    private generalFunctions: GeneralFunctions,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private azureOperationsService: AzureOperationsService,
    private azureTokenService: AzureTokenService,
    private confirmationDialogService: ConfirmationDialogService) { }

  ngOnInit(): void {
    // Note: Below 'queryParams' can be replaced with 'params' depending on your requirements
    this.activatedRoute.queryParams.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.filterId = id;
      }

    });
    this.roles = this.authService.getSpecialRoles();
    this.getNextRecordsBatch(true);
  }

  getNextRecordsBatch(isForwardMove: boolean = true) {
    if (isForwardMove) {
      this.pageNo++;
    }
    else {
      this.pageNo--;
      if (this.pageNo == 0) {
        this.pageNo = 1;
      }
    }

    if (this.filterId == 0) {
      this.azureNamespaceService.getRecordCounts()
        .subscribe((data: number) => {
          this.totalRecords = data;
          this.pager = this.generalFunctions.paginate(this.totalRecords, this.pageNo, PAGE_RESULT_SIZE, 5);

          if (data > 0) {
            this.azureNamespaceService.getNamespaces(this.pageNo)
              .subscribe((dataList: AzureNamespaceDisplay[]) => {
                if (dataList && dataList.length > 0) {
                  this.namespaces = dataList;
                }
              });
          }
        });
    }
    else {
      this.azureNamespaceService.getNamespaceById(this.filterId)
        .subscribe((data: AzureNamespaceDisplay) => {
          this.namespaces = new Array();
          this.namespaces.push(data);
        })
    }
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  openModal(id: number = 0) {
    const modalRef = this.modalService.open(EditNamespaceComponent, { windowClass: 'modal-holder', centered: true });
    modalRef.componentInstance.azureNamespaceId = id;
    if (id > 0) {

      var curNamespace = this.namespaces.find(a => a.Id === id);

      if (curNamespace) {
        modalRef.componentInstance.currentNamespace = curNamespace;
        modalRef.componentInstance.setFormValues();
      }
    }

    /*
    * | FOLLOWING WILL EMIT TO SUGGEST
    * | NAMESPACE IS CREATED OR UPDATED SUCCESSFULLY
    */
    modalRef.componentInstance.isNamespaceSaved.subscribe((value) => {
      this.isShowSuccess = true;
      this.successMessage = "Azure namespace saved/updated successfully";
      this.refreshRecords();
    });

  }

  closeSuccess() {
    this.isShowSuccess = false;
  }

  closeError() {
    this.isShowError = false;
  }

  refreshRecords() {
    if (this.pageNo == 0) {
      this.pageNo = 1;
    }

    this.azureNamespaceService.getRecordCounts()
      .subscribe((data: number) => {
        this.totalRecords = data;
        this.pager = this.generalFunctions.paginate(this.totalRecords, this.pageNo, PAGE_RESULT_SIZE, 5);


        if (data > 0) {
          this.azureNamespaceService.getNamespaces(this.pageNo)
            .subscribe((dataList: AzureNamespaceDisplay[]) => {
              if (dataList && dataList.length > 0) {
                this.namespaces = dataList;
              }
            });
        }
      });
  }

  fetchSpecificPage(pgNo: number) {
    if (pgNo == this.pageNo) {
      return;
    }

    this.pageNo = pgNo;
    this.refreshRecords();
  }

  public callThis() {

  }

  public refreshNamespaceWithAzure(id: number) {
    let namespace = this.namespaces.find(x => x.Id === id);
    if (namespace) {
      if (this.azureTokenService.isLoggedIn()) {
        //USER IS ALREADY LOGGED IN
        this.refreshNamespace(namespace);
      }
      else {
        this.azureTokenService.login().then(() => {
          this.refreshNamespace(namespace);
        }).catch((err) => {
          this.isShowError = true;
          this.errorMessage = "Could not log you into Azure account, check developer console for more details";
        });
      }
    }

  }

  private refreshNamespace(namespace: AzureNamespaceDisplay) {
    this.azureOperationsService.getKeysForANamespace(namespace.SubscriptionId, namespace.AzureNamespace, namespace.ResourceGroup)
      .pipe(first())
      .subscribe(
        (data: KeysForANamespace) => {
          let updateKeysRequest: UpdateKeysRequest = new UpdateKeysRequest();
          updateKeysRequest.id = namespace.Id;
          updateKeysRequest.keyName = data.keyName;
          updateKeysRequest.keyValue = data.primaryKey;

          this.azureNamespaceService.updateKeys(updateKeysRequest)
            .pipe(first())
            .subscribe(response => {
              namespace.Status = true;
            })
        },
        (error: AzureErrorResponse) => {
          this.isShowError = true;
          this.errorMessage = error.message;
        }
      )
  }

  deleteNamespace(id: number) {
    this.confirmationDialogService.confirm('Please confirm', 'Are you sure you want to delete this namespace?')
      .then((confirmed) => {
        if (confirmed) {

          this.azureNamespaceService.deleteNamespace(id)
            .subscribe((data: boolean) => {
              if (data) {
                this.isShowSuccess = true;
                this.successMessage = "Namespace deleted successfully";
                this.refreshRecords();
              }
              else {
                this.isShowError = true;
                this.errorMessage = "Something went wrong";
              }
            });

        }
        else {
          return;
        }
      }).catch((error) => {
        return;
      })
  }

  removeFilter() {
    this.filterId = 0;
    this.router.navigate(['/namespaces']);
    this.getNextRecordsBatch(false);
  }

}
